import React, { useState, useEffect, useCallback, memo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Typography, CircularProgress, Alert, Snackbar,
  IconButton, List, ListItem, ListItemIcon, ListItemText,
  Collapse, Paper, Button
} from '@mui/material';
import {
  ExpandMore, ChevronRight, Business, Groups,
  Group, Person, Refresh
} from '@mui/icons-material';
import { getAllClients, getClientById } from '../api/services/userService/Client';
import { getAllDepartments, getDepartmentById } from '../api/services/userService/Department';
import { getAllTeams, getTeamById } from '../api/services/userService/Team';
import { getAllUsers, getCurrentUser } from '../api/services/userService/User';

// Memoized Components for better performance
const ClientItem = memo(({ client, departments, teams, users, expanded, onToggle }) => {
  const clientId = client.id;
  const clientNodeId = `client-${clientId}`;
  const isClientExpanded = expanded[clientNodeId] || false;
  
  // Find departments for this client
  const clientDepartments = departments.filter(dept => dept.clientId === clientId);
  
  // Count users assigned to this client (including through departments/teams)
  const clientUsers = users.filter(user => 
    user.clientIds?.includes(clientId) || 
    user.clients?.some(c => c.id === clientId) || 
    user.clientId === clientId
  );

  // Find direct client users (users assigned to client but not to any departments)
  const directClientUsers = clientUsers.filter(user => 
    !user.departments?.length && !user.departmentIds?.length && !user.departmentId
  );

  return (
    <React.Fragment>
      <ListItem 
        button 
        onClick={() => onToggle(clientNodeId)}
        sx={{ 
          bgcolor: 'background.paper',
          borderRadius: 1,
          mb: 1,
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <ListItemIcon>
          <Business color="primary" />
        </ListItemIcon>
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1">{client.name}</Typography>
              <Typography variant="caption" sx={{ ml: 1 }}>
                ({clientDepartments.length} departments, {clientUsers.length} users)
              </Typography>
            </Box>
          } 
        />
        {isClientExpanded ? <ExpandMore /> : <ChevronRight />}
      </ListItem>
      
      {isClientExpanded && (
        <Collapse in={true} timeout="auto">
          <List component="div" disablePadding>
            {clientDepartments.map(department => (
              <DepartmentItem 
                key={department.id}
                department={department}
                teams={teams}
                users={users}
                expanded={expanded}
                onToggle={onToggle}
              />
            ))}
            {directClientUsers.length > 0 && (
              <>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        Direct Client Users ({directClientUsers.length})
                      </Typography>
                    } 
                  />
                </ListItem>
                {directClientUsers.map(user => {
                  // Get the user's role in this client
                  const userClient = user.clients?.find(c => c.id === clientId);
                  const userRole = userClient?.role || 'Member';
                  
                  return (
                    <ListItem 
                      key={`direct-client-user-${user.id}`}
                      sx={{ pl: 8 }}
                    >
                      <ListItemIcon>
                        <Person fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="body2">
                            {user.fullName || user.email}
                            {user.isSuperAdmin && " (Super Admin)"}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Role: {userRole}
                          </Typography>
                        } 
                      />
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
});

const DepartmentItem = memo(({ department, teams, users, expanded, onToggle }) => {
  const departmentId = department.id;
  const departmentNodeId = `dept-${departmentId}`;
  const isDepartmentExpanded = expanded[departmentNodeId] || false;
  
  // Find teams for this department
  const departmentTeams = teams.filter(team => team.departmentId === departmentId);
  
  // Count users assigned to this department (including through teams)
  const departmentUsers = users.filter(user => 
    user.departmentIds?.includes(departmentId) ||
    user.departments?.some(d => d.id === departmentId) || 
    user.departmentId === departmentId
  );

  // Find direct department users (users assigned to department but not to any teams)
  const directDepartmentUsers = departmentUsers.filter(user => 
    !user.teams?.length && !user.teamIds?.length && !user.teamId
  );

  return (
    <React.Fragment>
      <ListItem 
        button 
        onClick={() => onToggle(departmentNodeId)}
        sx={{ 
          pl: 4,
          borderRadius: 1,
          mb: 0.5,
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <ListItemIcon>
          <Groups color="secondary" />
        </ListItemIcon>
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">{department.name}</Typography>
              <Typography variant="caption" sx={{ ml: 1 }}>
                ({departmentTeams.length} teams, {departmentUsers.length} users)
              </Typography>
            </Box>
          } 
        />
        {isDepartmentExpanded ? <ExpandMore /> : <ChevronRight />}
      </ListItem>
      
      {isDepartmentExpanded && (
        <Collapse in={true} timeout="auto">
          <List component="div" disablePadding>
            {departmentTeams.map(team => (
              <TeamItem 
                key={team.id}
                team={team}
                users={users}
                expanded={expanded}
                onToggle={onToggle}
              />
            ))}
            {directDepartmentUsers.length > 0 && (
              <>
                <ListItem sx={{ pl: 8 }}>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        Direct Department Users ({directDepartmentUsers.length})
                      </Typography>
                    } 
                  />
                </ListItem>
                {directDepartmentUsers.map(user => {
                  // Get the user's role in this department
                  const userDepartment = user.departments?.find(d => d.id === departmentId);
                  const userRole = userDepartment?.role || 'Member';
                  
                  return (
                    <ListItem 
                      key={`direct-dept-user-${user.id}`}
                      sx={{ pl: 12 }}
                    >
                      <ListItemIcon>
                        <Person fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="body2">
                            {user.fullName || user.email}
                            {user.isSuperAdmin && " (Super Admin)"}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Role: {userRole}
                          </Typography>
                        } 
                      />
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
});

const TeamItem = memo(({ team, users, expanded, onToggle }) => {
  const teamId = team.id;
  const teamNodeId = `team-${teamId}`;
  const isTeamExpanded = expanded[teamNodeId] || false;
  
  // Filter users for this team using the new data structure
  const teamUsers = users.filter(user => 
    user.teamIds?.includes(teamId) ||
    user.teams?.some(t => t.id === teamId) || 
    user.teamId === teamId
  );
  
  return (
    <React.Fragment>
      <ListItem 
        button 
        onClick={() => onToggle(teamNodeId)}
        sx={{ 
          pl: 8,
          borderRadius: 1,
          mb: 0.5,
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <ListItemIcon>
          <Group color="info" />
        </ListItemIcon>
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">{team.name}</Typography>
              <Typography variant="caption" sx={{ ml: 1 }}>
                ({teamUsers.length} users)
              </Typography>
            </Box>
          } 
        />
        {isTeamExpanded ? <ExpandMore /> : <ChevronRight />}
      </ListItem>
      
      {isTeamExpanded && (
        <Collapse in={true} timeout="auto">
          <List component="div" disablePadding>
            {teamUsers.map(user => {
              // Get the user's role in this team
              const userTeam = user.teams?.find(t => t.id === teamId);
              const userRole = userTeam?.role || userTeam?.team_role || 'Member';
              
              return (
                <ListItem 
                  key={`user-${user.id}-team-${teamId}`}
                  sx={{ pl: 12 }}
                >
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2">
                        {user.fullName || user.email}
                        {user.isSuperAdmin && " (Super Admin)"}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Role: {userRole}
                      </Typography>
                    } 
                  />
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
});

export default function HierarchyView({ userPermissions }) {
  const { keycloak } = useKeycloak();
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleToggle = useCallback((nodeId) => {
    setExpanded(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await keycloak.updateToken(30);
      
      let clientsData = [];
      let departmentsData = [];
      let teamsData = [];
      let usersData = [];

      if (userPermissions?.isSuperAdmin) {
        const [clientResponse, departmentResponse, teamResponse, userResponse] = await Promise.all([
          getAllClients({
            pagination: {
              page: 1,
              pageSize: 1000
            },
            sorting: {
              sort_by: 'SORT_BY_NAME',
              order: 'SORT_ORDER_ASCENDING'
            }
          }, keycloak.token),
          
          getAllDepartments({
            pagination: {
              page: 1,
              pageSize: 2000
            },
            sorting: {
              sort_by: 'SORT_BY_NAME',
              order: 'SORT_ORDER_ASCENDING'
            }
          }, keycloak.token),
          
          getAllTeams({
            pagination: {
              page: 1,
              pageSize: 5000
            },
            sorting: {
              sort_by: 'SORT_BY_NAME',
              order: 'SORT_ORDER_ASCENDING'
            }
          }, keycloak.token),
          
          getAllUsers({
            pagination: {
              page: 1,
              pageSize: 10000
            },
            sorting: {
              sort_by: 'SORT_BY_NAME',
              order: 'SORT_ORDER_ASCENDING'
            }
          }, keycloak.token)
        ]);

        if (!clientResponse.success) {
          throw new Error(clientResponse.error || 'Failed to fetch clients');
        }
        if (!departmentResponse.success) {
          throw new Error(departmentResponse.error || 'Failed to fetch departments');
        }
        if (!teamResponse.success) {
          throw new Error(teamResponse.error || 'Failed to fetch teams');
        }
        if (!userResponse.success) {
          throw new Error(userResponse.error || 'Failed to fetch users');
        }

        clientsData = clientResponse.clients || [];
        departmentsData = departmentResponse.departments || [];
        teamsData = teamResponse.teams || [];
        usersData = userResponse.users || [];

      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data:', userData);

        const allUsers = new Map();
        
        const currentUserRecord = {
          id: userData.id,
          fullName: userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.email,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          company: userData.company,
          position: userData.position,
          isSuperAdmin: userData.superAdmin,
          clientIds: userData.client ? [userData.client.client.id] : [],
          clientId: userData.client ? userData.client.client.id : null,
          clients: userData.client ? [{ id: userData.client.client.id, role: userData.client.client.role }] : [],
          departmentIds: userData.departments ? userData.departments.map(d => d.department.id) : [],
          departmentId: userData.departments && userData.departments.length > 0 ? userData.departments[0].department.id : null,
          departments: userData.departments ? userData.departments.map(d => ({ id: d.department.id, role: d.department.role })) : [],
          teamIds: userData.teams ? userData.teams.map(t => t.team.id) : [],
          teamId: userData.teams && userData.teams.length > 0 ? userData.teams[0].team.id : null,
          teams: userData.teams ? userData.teams.map(t => ({ id: t.team.id, role: t.team.role })) : []
        };
        
        allUsers.set(userData.id, currentUserRecord);
        
        if (userData.client) {
          const clientResponse = await getClientById(userData.client.client.id, keycloak.token);
          if (clientResponse.success) {
            clientsData.push(clientResponse.client);
          }
          
          if (userData.client.admins) {
            userData.client.admins.forEach(admin => {
              allUsers.set(admin.id, {
                id: admin.id,
                fullName: admin.fullName,
                email: admin.email,
                clientIds: [userData.client.client.id],
                clientId: userData.client.client.id,
                clients: [{ id: userData.client.client.id, role: 'admin' }],
                departmentIds: [],
                teamIds: []
              });
            });
          }
        }

        if (userData.departments && userData.departments.length > 0) {
          const departmentPromises = userData.departments.map(deptData => 
            getDepartmentById(deptData.department.id, keycloak.token)
          );
          const departmentResponses = await Promise.all(departmentPromises);
          
          departmentResponses.forEach((response, index) => {
            if (response.success) {
              const department = response.department;
              department.clientId = userData.client ? userData.client.client.id : null;
              departmentsData.push(department);
              
              const deptData = userData.departments[index];
              if (deptData.admins) {
                deptData.admins.forEach(admin => {
                  if (allUsers.has(admin.id)) {
                    const existingUser = allUsers.get(admin.id);
                    existingUser.departmentIds.push(deptData.department.id);
                    existingUser.departments = existingUser.departments || [];
                    existingUser.departments.push({ 
                      id: deptData.department.id, 
                      role: admin.id === userData.id ? deptData.department.role : 'admin' 
                    });
                    if (!existingUser.departmentId) {
                      existingUser.departmentId = deptData.department.id;
                    }
                  } else {
                    allUsers.set(admin.id, {
                      id: admin.id,
                      fullName: admin.fullName,
                      email: admin.email,
                      clientIds: userData.client ? [userData.client.client.id] : [],
                      clientId: userData.client ? userData.client.client.id : null,
                      departmentIds: [deptData.department.id],
                      departmentId: deptData.department.id,
                      departments: [{ id: deptData.department.id, role: admin.id === userData.id ? deptData.department.role : 'admin' }],
                      teamIds: []
                    });
                  }
                });
              }
            }
          });
        }

        if (userData.teams && userData.teams.length > 0) {
          const teamPromises = userData.teams.map(teamData => 
            getTeamById(teamData.team.id, keycloak.token)
          );
          const teamResponses = await Promise.all(teamPromises);
          
          teamResponses.forEach((response, index) => {
            if (response.success) {
              const team = response.team;
              const teamData = userData.teams[index];
              team.departmentId = teamData.team.departmentId;
              teamsData.push(team);
              
              if (teamData.admins) {
                teamData.admins.forEach(admin => {
                  if (allUsers.has(admin.id)) {
                    const existingUser = allUsers.get(admin.id);
                    existingUser.teamIds.push(teamData.team.id);
                    existingUser.teams = existingUser.teams || [];
                    existingUser.teams.push({ 
                      id: teamData.team.id, 
                      role: admin.id === userData.id ? teamData.team.role : 'admin' 
                    });
                    if (!existingUser.teamId) {
                      existingUser.teamId = teamData.team.id;
                    }
                  } else {
                    allUsers.set(admin.id, {
                      id: admin.id,
                      fullName: admin.fullName,
                      email: admin.email,
                      clientIds: userData.client ? [userData.client.client.id] : [],
                      clientId: userData.client ? userData.client.client.id : null,
                      departmentIds: userData.departments ? userData.departments.map(d => d.department.id) : [],
                      departmentId: userData.departments && userData.departments.length > 0 ? userData.departments[0].department.id : null,
                      teamIds: [teamData.team.id],
                      teamId: teamData.team.id,
                      teams: [{ id: teamData.team.id, role: admin.id === userData.id ? teamData.team.role : 'admin' }]
                    });
                  }
                });
              }
            }
          });
        }

        usersData = Array.from(allUsers.values());
        console.log('Processed users for non-super admin:', usersData);
      }

      console.log('Fetched data:', {
        clients: clientsData.length,
        departments: departmentsData.length,
        teams: teamsData.length,
        users: usersData.length
      });

      console.log('Departments with clientId:', departmentsData.map(d => ({ id: d.id, name: d.name, clientId: d.clientId })));
      console.log('Teams with departmentId:', teamsData.map(t => ({ id: t.id, name: t.name, departmentId: t.departmentId })));

      setClients(clientsData);
      setDepartments(departmentsData);
      setTeams(teamsData);
      
      const processedUsers = usersData.map(user => ({
        ...user,
        fullName: user.full_name || user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        clientIds: user.clients ? user.clients.map(c => c.id) : (user.clientId ? [user.clientId] : user.clientIds || []),
        departmentIds: user.departments ? user.departments.map(d => d.id) : (user.departmentId ? [user.departmentId] : user.departmentIds || []),
        teamIds: user.teams ? user.teams.map(t => t.id) : (user.teamId ? [user.teamId] : user.teamIds || []),
        clientId: user.clients && user.clients.length > 0 ? user.clients[0].id : user.clientId,
        departmentId: user.departments && user.departments.length > 0 ? user.departments[0].id : user.departmentId,
        teamId: user.teams && user.teams.length > 0 ? user.teams[0].id : user.teamId
      }));
      
      setUsers(processedUsers);

      console.log('Processed users sample:', processedUsers.slice(0, 3));

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [keycloak.token]);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleRefresh = () => {
    setNotification({
      open: true,
      message: 'Refreshing hierarchy data...',
      severity: 'info'
    });
    fetchAllData();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading organization hierarchy...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              <Refresh sx={{ mr: 1 }} />
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Organization Hierarchy</Typography>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {!loading && !error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>Summary</Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business color="primary" />
              <Typography variant="body2">
                <strong>{clients.length}</strong> Clients
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Groups color="secondary" />
              <Typography variant="body2">
                <strong>{departments.length}</strong> Departments
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="info" />
              <Typography variant="body2">
                <strong>{teams.length}</strong> Teams
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              <Typography variant="body2">
                <strong>{users.length}</strong> Users
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {clients.length === 0 ? (
        <Alert severity="info">No data available in hierarchy</Alert>
      ) : (
        <Paper elevation={2} sx={{ p: 2, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <List component="nav" aria-label="organization hierarchy">
            {clients.map(client => (
              <ClientItem 
                key={client.id}
                client={client}
                departments={departments}
                teams={teams}
                users={users}
                expanded={expanded}
                onToggle={handleToggle}
              />
            ))}
            
            {/* Display users not assigned to any groups */}
            {(() => {
              const orphanedUsers = users.filter(user => 
                (!user.clients?.length && !user.clientIds?.length && !user.clientId) &&
                (!user.departments?.length && !user.departmentIds?.length && !user.departmentId) &&
                (!user.teams?.length && !user.teamIds?.length && !user.teamId)
              );
              
              if (orphanedUsers.length > 0) {
                return (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <ListItem>
                      <ListItemIcon>
                        <Person color="warning" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="body1" color="warning.main" fontWeight="bold">
                            Unassigned Users ({orphanedUsers.length})
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Users not assigned to any client, department, or team
                          </Typography>
                        } 
                      />
                    </ListItem>
                    {orphanedUsers.map(user => (
                      <ListItem 
                        key={`orphaned-user-${user.id}`}
                        sx={{ pl: 8 }}
                      >
                        <ListItemIcon>
                          <Person fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Typography variant="body2">
                              {user.fullName || user.email}
                              {user.isSuperAdmin && " (Super Admin)"}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Status: {user.enabled ? 'Active' : 'Inactive'}
                            </Typography>
                          } 
                        />
                      </ListItem>
                    ))}
                  </Box>
                );
              }
              return null;
            })()}
          </List>
        </Paper>
      )}

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
